import { ceil } from "@suse-o11y/ceil";
import { differenceInHours, hoursToMilliseconds } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";

import {
	Edition,
	type ServerSuppliedWebUIConfig,
	type UnlicensedReason,
	isExpiredSubscription,
	isUnlicensedSubscription,
} from "~/models/dto";

import { request } from "../generic/request";

import type { UseFullQueryOptions } from "./react-query";

export const queryUIConfig = {
	queryKey: ["server", "webuiconfig"],
	queryFn: () =>
		request<ServerSuppliedWebUIConfig>("/api/server/webuiconfig", {
			query: {
				timeoutSeconds: 15,
			},
		}),
	staleTime: Number.POSITIVE_INFINITY,
} satisfies UseFullQueryOptions<ServerSuppliedWebUIConfig, Error>;

export type LicenseError = undefined | UnlicensedReason | "expired" | "failure";

interface LicenseInfo {
	/**
	 * Number of days remaining before the license expires.
	 * Can be negative during the post-expiration tolerance period when the product remains functional.
	 * If `expiryTimestampMs` is undefined, this value is set to `Infinity`.
	 */
	daysLeftToExpire: number;
	error: LicenseError;
	isTrial: boolean;
	isCommunity: boolean;
}

export const selectEnableAi = ({ enableAi }: ServerSuppliedWebUIConfig) =>
	(import.meta.env.DEV && import.meta.env.VITE_AI_ASSISTANT === "true") || enableAi;

export const queryLicenseConfig = {
	...queryUIConfig,
	refetchOnMount: false,
	refetchOnReconnect: false,
	refetchOnWindowFocus: false,
	refetchOnTimelineSummaryUpdate: false,
	refetchInterval: hoursToMilliseconds(24),
	select: (config) => {
		const isTrial = config.webUIConfig.featureFlags.onlineTrial;
		const [error, daysLeftToExpire] = validateLicense(config);

		return {
			// we can't display that user has no days left to trial expire, but it's possible if it's configured incorrectly
			daysLeftToExpire: isTrial && !Number.isFinite(daysLeftToExpire) ? 14 : daysLeftToExpire,
			error,
			isTrial,
			isCommunity: config.edition === Edition.Community,
		};
	},
} satisfies UseFullQueryOptions<ServerSuppliedWebUIConfig, Error, LicenseInfo>;

export const queryLiveness = {
	queryKey: ["server", "status"],
	queryFn: () =>
		request<void>("/api/server/liveness", {
			query: {
				timeoutSeconds: 15,
			},
		}),
	staleTime: 0,
	cacheTime: 0,
} satisfies UseFullQueryOptions<void, Error>;

const calculateDaysLeftToExpire = (expiration: Time | undefined): number => {
	if (expiration == null) {
		return Number.POSITIVE_INFINITY;
	}

	return ceil(differenceInHours(expiration, Date.now()) / 24, 0);
};

const validateLicense = ({
	subscription,
}: ServerSuppliedWebUIConfig): [error: undefined | LicenseError, value: number] => {
	if (isUnlicensedSubscription(subscription)) {
		return [subscription.reason, Number.POSITIVE_INFINITY];
	}

	if (isExpiredSubscription(subscription)) {
		return ["expired", calculateDaysLeftToExpire(subscription.subscription.expiryTimestampMs)];
	}

	return [undefined, calculateDaysLeftToExpire(subscription.subscription.expiryTimestampMs)];
};
